import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import useConfirmation from '../common/useConfirmation';
import { interfaceError } from '../../i18n/errorMessages';
import toast from '../../i18n/toast';
import LocalizedMessage from '../common/LocalizedMessage';
import { history } from "../../redux/store/configureStore";
import * as clientLedgerActions from "../../redux/actions/clientLedgerActions";
import * as clientActions from "../../redux/actions/clientActions";
import { getById } from "../common/Helpers";
import ClientLedgerForm from "./ClientLedgerForm";
import ClientLedgerList from "./ClientLedgerList";
import { CLIENT_LEDGER_ENTRY_TYPES, PAYMENT_METHODS } from "./clientLedgerTypes";
import { normalizeClientLedgerEntry, validateClientLedgerEntry } from "./clientLedgerValidation";
import {
  buildManageRouteWithSchoolYear,
  getNormalizedSchoolYearStartYearFromSearch,
} from "./manageSchoolYearRouting";
import API from '../../services/ClientLedgerService';
import ClientBillingSummary, { billingYearLabel } from './ClientBillingSummary';
import ClientYearTransferForm from './ClientYearTransferForm';
import ClientYearAssignmentReview from './ClientYearAssignmentReview';
import { initialBillingView } from '../../redux/reducers/clientBillingViewReducer';

const formatDatePart = value => String(value).padStart(2, "0");

const currentDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${formatDatePart(date.getMonth() + 1)}-${formatDatePart(date.getDate())}`;
};

const currentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${formatDatePart(date.getMonth() + 1)}`;
};

const createEmptyEntry = (clientId, year) => ({
  clientId,
  schoolYearStartYear: year,
  type: CLIENT_LEDGER_ENTRY_TYPES.PAYMENT,
  date: currentDate(),
  settlementMonth: currentMonth(),
  amount: "",
  method: PAYMENT_METHODS.TRANSFER,
  note: "",
});

const resetEntry = (clientId, year) => createEmptyEntry(clientId, year);

export function ClientLedgerPage({
  clients,
  client,
  loadClients,
  loadClientLedger,
  saveClientLedgerEntry,
  deleteClientLedgerEntry,
  billingView,
  schoolId,
  loadClientBillingView,
}) {
  const { confirm, confirmation } = useConfirmation();
  const { t: tr } = useTranslation();
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const [allYears, setAllYears] = useState(false);
  const year = allYears ? undefined : activeSchoolYearStartYear;
  const scopeKey = clientLedgerActions.billingScopeKey(schoolId, client.id, year);
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const view = billingView.scopeKey === scopeKey ? billingView : initialBillingView;
  const {summary} = view;
  const [entry, setEntry] = useState(createEmptyEntry(client.id, activeSchoolYearStartYear));
  const [busy, setBusy] = useState(false);
  const [transfer, setTransfer] = useState(null);
  const [preview, setPreview] = useState(null);
  const [assignmentProgress, setAssignmentProgress] = useState(null);
  const [errors, setErrors] = useState({});
  const [editingEntryId, setEditingEntryId] = useState("");
  const entries = view.entries
    .filter(item => item.clientId === client.id)
    .sort((a, b) => (a.entryKey || "").localeCompare(b.entryKey || ""));

  useEffect(() => {
    let cancelled = false;
    setAssignmentProgress(null);
    if (summary && summary.assignmentStatus === 'applying' && summary.assignmentOperationId) {
      API.getAssignmentStatus(summary.assignmentOperationId, client.id).then(result => {
        if (!cancelled) setAssignmentProgress(result);
      }).catch(error => { if (!cancelled) setErrors({ saveError: error.message }); });
    }
    return () => { cancelled = true; };
  }, [summary, client.id, schoolId]);

  useEffect(() => {
    if (clients.length === 0) {
      loadClients().catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    }
  }, [clients.length, loadClients]);

  useEffect(() => {
    if (client.id) {
      loadClientBillingView(client.id, year).catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    }
  }, [client.id, schoolId, year, loadClientBillingView]);

  useEffect(() => {
    if (client.id) {
      setEntry(resetEntry(client.id, activeSchoolYearStartYear));
      setErrors({});
      setEditingEntryId("");
      setTransfer(null);
      setPreview(null);
    }
  }, [client.id, schoolId, activeSchoolYearStartYear]);

  const refresh = () => loadClientBillingView(client.id, year);
  const mutate = async action => {
    if (busy || !summary) return;
    setBusy(true);
    setErrors({});
    try {
      await action();
      if (currentScope.current !== scopeKey) return;
      setTransfer(null);
      setPreview(null);
      setEntry(resetEntry(client.id, activeSchoolYearStartYear));
      setEditingEntryId('');
      await refresh();
      await loadClientLedger();
    } catch (error) {
      if (currentScope.current !== scopeKey) return;
      setErrors({ saveError: error.message });
      await refresh().catch(() => {});
    } finally { setBusy(false); }
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setEntry(prevEntry => ({ ...prevEntry, [name]: value }));
  };

  const handleEdit = selectedEntry => {
    setEntry({
      ...selectedEntry,
      expectedRevision: summary.revision,
      amount: String(selectedEntry.amount),
    });
    setErrors({});
    setEditingEntryId(selectedEntry.id);
  };

  const handleCancelEdit = () => {
    setEntry(resetEntry(client.id, activeSchoolYearStartYear));
    setErrors({});
    setEditingEntryId("");
  };

  const handleDelete = async selectedEntry => {
    if (!(await confirm(tr("manage:extraDeleteThisEntry_21")))) return;

    mutate(() => deleteClientLedgerEntry({ ...selectedEntry, expectedRevision: summary.revision }));
  };

  const handleSubmit = event => {
    event.preventDefault();
    const validationErrors = validateClientLedgerEntry(entry);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const normalizedEntry = normalizeClientLedgerEntry(entry, Date.now().toString());

    mutate(() => saveClientLedgerEntry({ ...normalizedEntry, expectedRevision: entry.expectedRevision === undefined ? summary.revision : entry.expectedRevision }, !editingEntryId));
  };

  if (!client.id) {
    return <>{confirmation}<div className="manage-text">{tr("manage:loadingClient_127")}</div></>;
  }

  return (
    <>
      {confirmation}
      <div className="mb-5">
        <h2 className="manage-title">{tr('common:ledgerTitle', { name: `${client.firstName || ''} ${client.lastName || ''}`.trim() })}</h2>
        <p className="manage-text">{allYears ? tr('billing:yearScope.all') : `${tr('billing:yearScope.year')} ${billingYearLabel(year)}`}</p>
        <button type="button" disabled={busy} className="btn manage-button manage-teal-button mb-3 mr-2" onClick={() => setAllYears(value => !value)}>
          {allYears ? billingYearLabel(activeSchoolYearStartYear) : tr('billing:yearScope.all')}
        </button>
        <button type="button" className="btn manage-button manage-teal-button mb-3" disabled={busy} onClick={() => refresh().catch(error => setErrors({ saveError: error.message }))}>
          {tr('billing:yearScope.refresh')}
        </button>
        {view.error && <div role="alert" className="alert alert-danger"><LocalizedMessage isError value={view.error} /></div>}
        <ClientBillingSummary
          summary={summary}
          year={year}
          onYear={selectedYear => {
            setAllYears(false);
            history.push(buildManageRouteWithSchoolYear(location.pathname, location.search, selectedYear));
          }}
        />
        {summary && summary.assignmentStatus === 'ready' && summary.years.filter(item => item.balanceCents > 0).map(item => (
          <button
            key={item.schoolYearStartYear}
            type="button"
            className="btn manage-button manage-teal-button mb-3 mr-2"
            disabled={busy}
            onClick={() => setTransfer({ sourceYear: item.schoolYearStartYear, availableCents: item.balanceCents, requestId: `${Date.now()}-${Math.random().toString(36).slice(2)}` })}
          >
            {tr('billing:yearScope.transfer')} {billingYearLabel(item.schoolYearStartYear)}
          </button>
        ))}
        {summary && summary.assignmentStatus !== 'ready' && summary.assignmentStatus !== 'applying' && (
        <button
          type="button"
          className="btn manage-button manage-teal-button mb-3"
          disabled={busy}
          onClick={async () => {
            setBusy(true); try { const result = await API.previewAssignments(client.id); if (currentScope.current === scopeKey) setPreview(result); } catch (error) {
              if (currentScope.current === scopeKey) setErrors({ saveError: error.message });
            } finally { setBusy(false); }
          }}
        >
          {tr('billing:yearScope.review')}
        </button>
        )}
        {summary && summary.assignmentStatus === 'applying' && (
        <div className="alert alert-warning">
          <p>{tr('billing:yearScope.applying')}</p>
          {assignmentProgress && <p>{tr('billing:yearScope.progress', { completed: assignmentProgress.completed, count: assignmentProgress.count })}</p>}
          <button
            type="button"
            disabled={busy}
            className="btn manage-button manage-red-button mr-2"
            onClick={() => mutate(() => (summary.assignmentOperationId.endsWith('-rollback')
              ? API.rollbackAssignments({ clientId: client.id, operationId: summary.assignmentOperationId.replace(/-rollback$/, '') })
              : API.applyAssignments({ clientId: client.id, operationId: summary.assignmentOperationId.replace(/-apply$/, '') })))}
          >{tr('billing:yearScope.resume')}
          </button>
          <button
            type="button"
            disabled={busy}
            className="btn manage-button manage-teal-button"
            onClick={() => mutate(() => API.rollbackAssignments({ clientId: client.id,
              operationId: summary.assignmentOperationId.replace(/-rollback$/, '') }))}
          >{tr('billing:yearScope.rollback')}
          </button>
        </div>
        )}
        {preview && (
        <ClientYearAssignmentReview
          key={preview.operationId}
          preview={preview}
          busy={busy}
          onCancel={() => setPreview(null)}
          onSubmit={assignments => mutate(() => API.applyAssignments({ clientId: client.id, operationId: preview.operationId, expectedRevision: preview.revision, assignments }))}
        />
        )}
        {transfer && summary && (
        <ClientYearTransferForm
          key={transfer.requestId}
          {...transfer}
          summary={summary}
          busy={busy}
          onCancel={() => setTransfer(null)}
          onSubmit={input => mutate(() => {
            const payload = { ...input, clientId: client.id, sourceYear: transfer.sourceYear, requestId: transfer.requestId, expectedRevision: summary.revision };
            return transfer.reversal ? API.reverseTransfer(transfer.operationId, payload) : API.transfer(payload);
          })}
        />
        )}
        {errors.saveError && <div role="alert" className="alert alert-danger"><LocalizedMessage isError value={errors.saveError} /></div>}
        <fieldset disabled={busy || !summary || summary.assignmentStatus === 'applying'}>
          <ClientLedgerForm
            entry={entry}
            errors={errors}
            onChange={handleChange}
            onSubmit={handleSubmit}
            isEditing={!!editingEntryId}
            onCancel={handleCancelEdit}
          />
          <div className="mt-4">
            <ClientLedgerList
              entries={entries}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onReverse={selected => setTransfer({
                sourceYear: selected.type === 'YEAR_TRANSFER_OUT' ? selected.schoolYearStartYear : selected.schoolYearStartYear - 1,
                availableCents: Math.round(Math.abs(selected.amount) * 100),
                reversal: true,
                operationId: selected.operationId,
                requestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
              })}
            />
          </div>
        </fieldset>
        <button
          type="button"
          className="btn manage-button manage-teal-button mt-3"
          onClick={() => history.push(buildManageRouteWithSchoolYear(`/manage/client/${client.id}`, location.search, activeSchoolYearStartYear))}
        >{tr("manage:back_128")}
        </button>
      </div>
    </>
  );
}

ClientLedgerPage.propTypes = {
  clients: PropTypes.array.isRequired,
  client: PropTypes.object.isRequired,
  loadClients: PropTypes.func.isRequired,
  loadClientLedger: PropTypes.func.isRequired,
  saveClientLedgerEntry: PropTypes.func.isRequired,
  deleteClientLedgerEntry: PropTypes.func.isRequired,
  billingView: PropTypes.object.isRequired,
  schoolId: PropTypes.string,
  loadClientBillingView: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.match.params;
  return {
    clients: state.clients,
    client: getById(state.clients, id) || {},
    billingView: state.clientBillingView,
    schoolId: state.user.schoolId,
  };
};
ClientLedgerPage.defaultProps = { schoolId: null };

export default connect(mapStateToProps, {
  loadClients: clientActions.loadClients,
  loadClientLedger: clientLedgerActions.loadClientLedger,
  loadClientBillingView: clientLedgerActions.loadClientBillingView,
  saveClientLedgerEntry: clientLedgerActions.saveClientLedgerEntry,
  deleteClientLedgerEntry: clientLedgerActions.deleteClientLedgerEntry,
})(ClientLedgerPage);
